import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useQuery, getArticles } from "wasp/client/operations";

type GetArticlesResult = Awaited<ReturnType<typeof getArticles>>;
export type ArticleWithLikeCount = GetArticlesResult["articles"][number];
type DateFilter = "all" | "1d" | "1w" | "1m";
type SortBy = "newest" | "oldest" | "mostPositive" | "leastPositive";
type ArticleLocale = "de" | "en" | "all";

interface UseInfiniteArticlesFilters {
  locale: ArticleLocale;
  bookmarksOnly: boolean;
  dateFilter: DateFilter;
  provider: string;
  categories: string[];
  searchQuery: string;
  sortBy: SortBy;
}

interface UseInfiniteArticlesReturn {
  articles: ArticleWithLikeCount[];
  isLoading: boolean;
  isLoadingMore: boolean;
  error: Error | null;
  hasMore: boolean;
  total: number;
  loadMore: () => void;
  reset: () => void;
}

export function useInfiniteArticles(
  limit: number = 30,
  filters: UseInfiniteArticlesFilters,
): UseInfiniteArticlesReturn {
  const {
    locale = "en",
    bookmarksOnly = false,
    dateFilter = "all",
    provider = "all",
    categories = [],
    searchQuery = "",
    sortBy = "newest",
  } = filters;
  const [articles, setArticles] = useState<ArticleWithLikeCount[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const previousOffsetRef = useRef(0);
  const normalizedCategories = useMemo(
    () =>
      [
        ...new Set(
          categories
            .map((category) => category.trim())
            .filter((category) => category.length > 0),
        ),
      ].sort(),
    [categories],
  );
  const normalizedSearchQuery = useMemo(
    () => searchQuery.trim(),
    [searchQuery],
  );
  const filterKey = useMemo(
    () =>
      JSON.stringify({
        locale,
        bookmarksOnly,
        dateFilter,
        provider,
        categories: normalizedCategories,
        searchQuery: normalizedSearchQuery,
        sortBy,
      }),
    [
      locale,
      bookmarksOnly,
      dateFilter,
      provider,
      normalizedCategories,
      normalizedSearchQuery,
      sortBy,
    ],
  );
  const previousFilterKeyRef = useRef(filterKey);

  const {
    data,
    isLoading: initialLoading,
    error,
    refetch,
  } = useQuery(getArticles, {
    offset,
    limit,
    locale,
    bookmarksOnly,
    dateFilter,
    provider,
    categories: normalizedCategories,
    searchQuery: normalizedSearchQuery,
    sortBy,
  });

  // Reset pagination when server-side query-defining filters change.
  // Articles are kept until new data arrives to preserve scroll position.
  useEffect(() => {
    if (previousFilterKeyRef.current !== filterKey) {
      setOffset(0);
      setHasMore(true);
      setTotal(0);
      setIsLoadingMore(false);
      previousOffsetRef.current = 0;
      previousFilterKeyRef.current = filterKey;
    }
  }, [filterKey]);

  useEffect(() => {
    if (data && data.articles) {
      if (offset === 0) {
        // Initial load or reset
        setArticles(Array.isArray(data.articles) ? data.articles : []);
      } else if (offset > previousOffsetRef.current) {
        // Loading more - only append if offset increased
        setArticles((prev) => [
          ...prev,
          ...(Array.isArray(data.articles) ? data.articles : []),
        ]);
        setIsLoadingMore(false);
      }
      setHasMore(data.hasMore || false);
      setTotal(data.total || 0);
      previousOffsetRef.current = offset;
    } else if (data && !data.articles) {
      // Handle case where data exists but articles is undefined
      setArticles([]);
      setHasMore(false);
      setTotal(0);
      setIsLoadingMore(false);
    }
  }, [data, offset]);

  const loadMore = useCallback(() => {
    if (hasMore && !isLoadingMore && !initialLoading) {
      setIsLoadingMore(true);
      setOffset((prev) => prev + limit);
    }
  }, [hasMore, isLoadingMore, initialLoading, limit]);

  const reset = useCallback(() => {
    setOffset(0);
    setArticles([]);
    setHasMore(true);
    setTotal(0);
    setIsLoadingMore(false);
    previousOffsetRef.current = 0;
    previousFilterKeyRef.current = filterKey;
    refetch();
  }, [filterKey, refetch]);

  return {
    articles,
    isLoading: initialLoading && offset === 0 && articles.length === 0,
    isLoadingMore,
    error,
    hasMore,
    total,
    loadMore,
    reset,
  };
}
